import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    useTheme,
    TextField,
    Button,
    Avatar,
    FormControlLabel,
    Switch,
    Autocomplete
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import AdminOSLayout from '../../../components/layout/AdminOSLayout';
import api from '../../../services/api';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import SchoolIcon from '@mui/icons-material/School';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import VideoLabelIcon from '@mui/icons-material/VideoLabel';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import FilterListIcon from '@mui/icons-material/FilterList';
import StarIcon from '@mui/icons-material/Star';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import LottieLoading from '../../../components/common/LottieLoading';
import { Organization } from '../../../types/organization';

const StatisticsPage = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<any>(null);
    const [cronLogs, setCronLogs] = useState<any[]>([]);

    // Filters
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    // Toggles
    const [showDuration, setShowDuration] = useState(false);

    useEffect(() => {
        fetchOrganizations();
        fetchStats();
    }, []);

    const fetchOrganizations = async () => {
        try {
            const res = await api.get('/api/organizations');
            setOrganizations(res.data);
        } catch (err) {
            console.error('Failed to fetch organizations', err);
        }
    };

    const fetchStats = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams();
            if (selectedOrg) queryParams.append('organizationId', selectedOrg._id);
            if (startDate) queryParams.append('startDate', startDate);
            if (endDate) queryParams.append('endDate', endDate);

            const [statsRes, cronRes] = await Promise.all([
                api.get(`/api/stats/dashboard?${queryParams.toString()}`),
                api.get('/api/stats/cron-logs')
            ]);
            setStats(statsRes.data);
            setCronLogs(cronRes.data);
        } catch (err) {
            console.error('Error fetching stats', err);
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilters = () => {
        fetchStats();
    };

    if (loading && !stats) {
        return (
            <AdminOSLayout>
                <LottieLoading message="Chargement des statistiques..." />
            </AdminOSLayout>
        );
    }

    if (!stats) return null;

    // Helper to format duration
    const formatDuration = (ms: number) => {
        const hours = Math.floor(ms / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}h ${minutes}m`;
    };

    // --- Chart Configurations ---

    // 1. Connection Activity
    const activityCategories = stats.activity.logins.map((d: any) => format(new Date(d._id), 'dd/MM'));
    const loginChartOptions: ApexCharts.ApexOptions = {
        chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false } },
        stroke: { curve: 'smooth', width: 3 },
        xaxis: { categories: activityCategories, labels: { style: { colors: '#64748b' } } },
        yaxis: {
            labels: {
                style: { colors: '#64748b' },
                formatter: (value) => showDuration ? formatDuration(value) : value.toFixed(0)
            }
        },
        colors: [showDuration ? '#8b5cf6' : '#3b82f6'],
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.45,
                opacityTo: 0.05,
            }
        },
        dataLabels: { enabled: false },
        tooltip: {
            y: {
                formatter: (value) => showDuration ? formatDuration(value) : `${value} connexions`
            }
        },
        grid: { borderColor: '#f1f5f9' }
    };

    const loginChartSeries = [{
        name: showDuration ? 'Durée Totale' : 'Connexions',
        data: stats.activity.logins.map((d: any) => showDuration ? d.totalDurationMs : d.count)
    }];

    // 2. Progression
    const progressionCategories = stats.progression.map((d: any) => format(new Date(d._id), 'dd/MM'));
    const progressionChartOptions: ApexCharts.ApexOptions = {
        chart: { type: 'bar', stacked: true, toolbar: { show: false } },
        colors: ['#3b82f6', '#10b981'],
        xaxis: { categories: progressionCategories, labels: { style: { colors: '#64748b' } } },
        legend: { position: 'top', horizontalAlign: 'right' },
        plotOptions: {
            bar: { borderRadius: 4, columnWidth: '40%' }
        },
        grid: { borderColor: '#f1f5f9' }
    };

    const progressionChartSeries = [
        { name: 'Démarré', data: stats.progression.map((d: any) => d.started) },
        { name: 'Terminé', data: stats.progression.map((d: any) => d.completed) }
    ];

    // 3. Role Distribution
    const roleChartOptions: ApexCharts.ApexOptions = {
        labels: Object.keys(stats.users.byRole).map(r => r.toUpperCase()),
        colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'],
        legend: { position: 'bottom' },
        stroke: { width: 0 },
        plotOptions: { pie: { donut: { size: '70%' } } }
    };
    const roleChartSeries = Object.values(stats.users.byRole) as number[];

    // 4. Email Status
    const emailChartOptions: ApexCharts.ApexOptions = {
        labels: Object.keys(stats.emails.byStatus).map(s => s.toUpperCase()),
        colors: ['#10b981', '#f59e0b', '#ef4444'],
        legend: { position: 'bottom' },
        stroke: { width: 0 }
    };
    const emailChartSeries = Object.values(stats.emails.byStatus) as number[];

    return (
        <AdminOSLayout>
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
                p: { xs: 2, md: 4 }
            }}>
                {/* Hero Header */}
                <Paper
                    elevation={0}
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 4,
                        p: { xs: 3, md: 5 },
                        mb: 4,
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56, backdropFilter: 'blur(10px)' }}>
                                <BarChartIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                                    Tableau de bord d'Analyse
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Vue d'ensemble et métriques de performance
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={fetchStats}
                            sx={{
                                bgcolor: 'white', color: '#764ba2', fontWeight: 700, borderRadius: 3, textTransform: 'none',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                            }}
                        >
                            Actualiser
                        </Button>
                    </Box>
                </Paper>

                {/* Filters Bar */}
                <Paper sx={{ p: 3, mb: 4, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', width: '100%' }}>
                    <Grid container spacing={3} alignItems="center" width="100%" justifyContent="space-between">
                        <Grid size={{ xs: 12, md: 5 }} sx={{ width: '20%' }}>
                            <Autocomplete
                                fullWidth
                                sx={{ width: 'auto' }}
                                options={organizations}
                                getOptionLabel={(option) => option.name}
                                value={selectedOrg}
                                onChange={(_, newValue) => setSelectedOrg(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Filtrer par Organisation"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <BusinessIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                                                    {params.InputProps.startAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2.5 }} sx={{ width: '20%' }}>
                            <TextField
                                sx={{ width: '100%' }}
                                type="date"
                                label="Date de début"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2.5 }} sx={{ width: '20%' }}>
                            <TextField
                                sx={{ width: '100%' }}
                                type="date"
                                label="Date de fin"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }} sx={{ width: '20%' }}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleApplyFilters}
                                startIcon={<FilterListIcon />}
                                sx={{
                                    py: 1.5,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    borderRadius: 2
                                }}
                            >
                                Appliquer
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {/* KPI Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }} justifyContent={'space-between'}>
                    {[
                        { icon: <PeopleIcon />, label: 'Utilisateurs', value: stats.users.total, color: 'primary' },
                        { icon: <BusinessIcon />, label: 'Organisations', value: stats.organizations.total, color: 'success' },
                        { icon: <EmailIcon />, label: 'Emails Envoyés', value: stats.emails.total, color: 'warning' },
                        { icon: <SchoolIcon />, label: 'Inscriptions', value: stats.enrollments.total, color: 'info' },
                        { icon: <ErrorIcon />, label: 'Erreurs hier', value: stats.kpis?.errorsYesterday ?? 0, color: 'error' },
                        { icon: <StarIcon />, label: 'Top Apprenant', value: stats.kpis?.mostActiveLearner ?? 'N/A', color: 'secondary' }
                    ].map((kpi, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                            <Card sx={{ borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                                <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <Box sx={{ p: 2, borderRadius: 3, bgcolor: `${kpi.color}.light`, color: `${kpi.color}.main`, display: 'flex' }}>
                                        {React.cloneElement(kpi.icon as React.ReactElement, { fontSize: 'large' })}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="h5" fontWeight="800" noWrap title={String(kpi.value)} sx={{ mb: 0.5 }}>{kpi.value}</Typography>
                                        <Typography variant="body2" color="text.secondary" fontWeight="500">{kpi.label}</Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Charts Row 1: Activity & Roles */}
                <Grid container spacing={3} sx={{ mb: 4 }} justifyContent={'space-between'}>
                    {/* Premier graphique : 70% de la largeur (environ 8.4/12 colonnes) */}
                    <Grid size={{ xs: 12, sm: 6 }} sx={{ width: { xs: '100%', sm: '75%' } }}>
                        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <VideoLabelIcon color="primary" sx={{ fontSize: 28 }} />
                                        <Typography variant="h5" fontWeight="700">Activité de Connexion</Typography>
                                    </Box>
                                    <FormControlLabel
                                        control={<Switch checked={showDuration} onChange={(e) => setShowDuration(e.target.checked)} />}
                                        label={<Typography variant="body1" fontWeight="500">Voir Temps (Durée)</Typography>}
                                    />
                                </Box>
                                <ReactApexChart options={loginChartOptions} series={loginChartSeries} type="area" height={350} />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Deuxième graphique : 30% de la largeur (environ 3.6/12 colonnes) */}
                    <Grid size={{ xs: 12, sm: 6 }} sx={{ width: { xs: '100%', sm: '22%' } }}>
                        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight="700" gutterBottom>Répartition par Rôle</Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
                                    <ReactApexChart options={roleChartOptions} series={roleChartSeries} type="donut" width="100%" />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>


                {/* Charts Row 2: Progression & Emails */}
                <Grid container spacing={3} justifyContent={'space-between'}>
                    <Grid size={{ xs: 12, sm: 6 }} sx={{ width: { xs: '100%', sm: '75%' } }}>
                        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                    <TimelineIcon color="success" sx={{ fontSize: 28 }} />
                                    <Typography variant="h5" fontWeight="700">Suivi des Progressions</Typography>
                                </Box>
                                <ReactApexChart options={progressionChartOptions} series={progressionChartSeries} type="bar" height={350} />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }} sx={{ width: { xs: '100%', sm: '22%' } }}>
                        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight="700" gutterBottom>Statut des Emails</Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
                                    <ReactApexChart options={emailChartOptions} series={emailChartSeries} type="pie" width="100%" />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* CRON Logs Section */}
                <Box sx={{ mt: 5 }}>
                    <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <ScheduleIcon color="primary" />
                            <Typography variant="h6" fontWeight="700">Historique des Jobs CRON</Typography>
                        </Box>
                        {/* Table implementation kept simple for brevity, can be enhanced similar to ErrorPage */}
                        <Box sx={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '2px solid #f1f5f9' }}>
                                        <th style={{ padding: '12px 16px', color: '#64748b' }}>Date</th>
                                        <th style={{ padding: '12px 16px', color: '#64748b' }}>Nom du Job</th>
                                        <th style={{ padding: '12px 16px', color: '#64748b' }}>Statut</th>
                                        <th style={{ padding: '12px 16px', color: '#64748b' }}>Message</th>
                                        <th style={{ padding: '12px 16px', color: '#64748b' }}>Durée</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cronLogs.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                                                Aucune exécution enregistrée
                                            </td>
                                        </tr>
                                    ) : (
                                        cronLogs.map((log) => (
                                            <tr key={log._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                                    {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm', { locale: fr })}
                                                </td>
                                                <td style={{ padding: '12px 16px', fontWeight: 600 }}>{log.jobName}</td>
                                                <td style={{ padding: '12px 16px' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {log.status === 'success' && <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />}
                                                        {log.status === 'failure' && <ErrorIcon sx={{ color: '#ef4444', fontSize: 18 }} />}
                                                        {log.status === 'partial' && <WarningIcon sx={{ color: '#f59e0b', fontSize: 18 }} />}
                                                        <Typography variant="body2" sx={{
                                                            textTransform: 'capitalize',
                                                            color: log.status === 'success' ? '#10b981' : (log.status === 'failure' ? '#ef4444' : '#f59e0b')
                                                        }}>
                                                            {log.status}
                                                        </Typography>
                                                    </Box>
                                                </td>
                                                <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#475569' }}>{log.message}</td>
                                                <td style={{ padding: '12px 16px', color: '#94a3b8' }}>{log.durationMs}ms</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </Box>
                    </Paper>
                </Box>
            </Box>
        </AdminOSLayout>
    );
};

export default StatisticsPage;
